/**
 * UniAlumniMentors — student-facing card on /uni/{id}. Lists
 * Verified-Partner-nominated alumni for this uni and lets a signed-in
 * student request a 15-min chat (backend routes the email to the
 * mentor's private inbox — the student never sees the address).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Handshake, GraduationCap, Linkedin, Loader2 } from "lucide-react";


export default function UniAlumniMentors({ universityId }) {
  const { user, openSignIn } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMentor, setOpenMentor] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/universities/${universityId}/alumni-mentors`);
        if (!cancelled) setRows(data.items || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [universityId]);

  if (loading || rows.length === 0) return null;

  const requestIntro = async () => {
    if (!user) { openSignIn?.(); return; }
    setSending(true);
    try {
      const { data } = await api.post("/alumni-mentors/intro-request", {
        mentor_id: openMentor.id,
        student_message: message.trim(),
      });
      if (data.status === "sent") {
        toast.success(`Sent — ${openMentor.alumni_name} will reply directly`);
      } else {
        toast.error("We logged your request but couldn't email the mentor. They'll be notified separately.");
      }
      setOpenMentor(null);
      setMessage("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === "no_talent_profile") {
        toast.error("Build your Talent Profile first — the mentor sees a summary");
      } else {
        toast.error(typeof d === "string" ? d : "Failed");
      }
    } finally { setSending(false); }
  };

  return (
    <section className="mb-8" data-testid="uni-alumni-mentors">
      <div className="flex items-center gap-2 text-emerald-800 mb-1">
        <Handshake className="h-4 w-4" />
        <span className="text-[10px] font-underground-sc tracking-widest">
          Alumni ready to mentor · {rows.length}
        </span>
      </div>
      <h3 className="font-editorial text-2xl text-stone-900 mb-3">
        Book 15 min with a grad
      </h3>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((m) => (
          <li
            key={m.id}
            className="p-4 rounded-xl border border-stone-200 bg-white flex flex-col"
            data-testid={`uni-alumni-card-${m.id}`}
          >
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-emerald-700" />
              <div className="font-medium text-stone-900 text-sm truncate">{m.alumni_name}</div>
            </div>
            <div className="mt-0.5 text-[11px] font-mono text-stone-500 truncate">
              {m.role}
              {m.grad_year ? ` · '${String(m.grad_year).slice(-2)}` : ""}
            </div>
            <div className="mt-0.5 text-[10px] font-underground-sc tracking-widest text-[#4C1D95] truncate">
              @ {m.firm_slug}
            </div>
            {m.mentor_note && (
              <p className="mt-2 text-[12px] text-stone-600 leading-relaxed line-clamp-3">
                {m.mentor_note}
              </p>
            )}
            <div className="mt-auto pt-3 flex items-center justify-between gap-2">
              <Button
                size="sm"
                onClick={() => setOpenMentor(m)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid={`uni-alumni-request-${m.id}`}
              >
                Request 15-min chat
              </Button>
              {m.linkedin_url && (
                <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-[11px] text-[#0A66C2] hover:underline"
                   data-testid={`uni-alumni-linkedin-${m.id}`}>
                  <Linkedin className="h-3 w-3" /> LinkedIn
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={!!openMentor} onOpenChange={(v) => { if (!v) { setOpenMentor(null); setMessage(""); } }}>
        <DialogContent data-testid="uni-alumni-request-dialog">
          <DialogHeader>
            <DialogTitle>Request 15 min with {openMentor?.alumni_name}</DialogTitle>
            <DialogDescription>
              We'll email them your Talent Profile summary (skills, sectors,
              grad year). They reply directly to your inbox to schedule.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            maxLength={500}
            placeholder="Optional: one line about why you'd like a chat"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="uni-alumni-request-message"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMentor(null)} disabled={sending}>Cancel</Button>
            <Button onClick={requestIntro} disabled={sending} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="uni-alumni-request-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
